import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface CareerRecommendation {
  recommended_occupation_code: string;
  occupation_title: string;
  recommendation_score: number;
  salary_improvement_potential: number;
  transition_difficulty: string;
  growth_outlook_score: number;
  skill_overlap_score: number;
}

interface CareerRecommendationsResponse {
  recommendations: CareerRecommendation[];
  meta: {
    primary_occupation: string;
    area: string;
    total_analyzed: number;
    computation_timestamp: string;
  };
}

export const useCareerRecommendations = (
  occupationCode: string, 
  areaCode: string, 
  limit: number = 6
) => {
  // OEWS broad group codes end in 0 (e.g. 29-1120 Therapists) and have no
  // rows in vw_agg_year_area_occ, so api-career-recommendations 500s on
  // them. Curated broad-group pages show aggregated wage data instead.
  const isBroadGroupSoc = occupationCode.endsWith('0');

  return useQuery<CareerRecommendation[]>({
    queryKey: ['career-recommendations', occupationCode, areaCode, limit],
    queryFn: async (): Promise<CareerRecommendation[]> => {
      if (!occupationCode || !areaCode) {
        return [];
      }

      const { data, error } = await supabase.functions.invoke('api-career-recommendations', {
        body: {
          occupation_code: occupationCode,
          area_code: areaCode,
          limit
        }
      });

      if (error) {
        console.error('Career recommendations error:', error);
        throw error;
      }

      const response = data as CareerRecommendationsResponse;
      
      // Filter out low-quality recommendations and sort by score
      return response.recommendations
        ?.filter(rec => rec.recommendation_score >= 30) // Only show relevant recommendations
        ?.sort((a, b) => b.recommendation_score - a.recommendation_score) || [];
    },
    staleTime: 1000 * 60 * 60 * 2, // 2 hours cache
    gcTime: 1000 * 60 * 60 * 4, // 4 hours
    enabled: !!occupationCode && !!areaCode && !isBroadGroupSoc,
  });
};